/**
 * Date range: optional presets plus two date inputs, in a popover.
 * Pass `presets={['custom']}` for From/To + Apply only (no Today/Yesterday list).
 *
 * Presentational. It emits ISO days (`YYYY-MM-DD`) and holds no state beyond
 * the two custom inputs the operator is mid-way through typing — the caller
 * owns the range, because in this console the range lives in the URL and a
 * component that also held it would be a second source of truth.
 *
 * Weeks start on MONDAY. That is not a locale preference here: this is a
 * business that settles on a Monday, so "last week" has to mean the week that
 * is being settled. The boundary maths is `(day + 6) % 7` everywhere, once.
 *
 * Every date is built from LOCAL parts (getFullYear/getMonth/getDate), never
 * `toISOString()`. In IST that shift is 5h30m, which silently moves "today"
 * onto yesterday for anyone working before half past five in the morning.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Button, cx } from './core'
import { Icon } from './Icon'
import { Popover } from './overlay'

export type DateRangePresetId =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | 'this-month'
  | 'last-month'
  | 'last-7'
  | 'last-30'
  | 'last-90'
  | 'custom'

export interface DateRangeValue {
  /** Inclusive ISO day. */
  from: string
  /** Inclusive ISO day. */
  to: string
}

export const DATE_RANGE_PRESETS: { id: DateRangePresetId; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this-week', label: 'This week' },
  { id: 'last-week', label: 'Last week' },
  { id: 'this-month', label: 'This month' },
  { id: 'last-month', label: 'Last month' },
  { id: 'last-7', label: 'Last 7 days' },
  { id: 'last-30', label: 'Last 30 days' },
  { id: 'last-90', label: 'Last 90 days' },
  { id: 'custom', label: 'Custom' },
]

/** Local-parts ISO day. Never `toISOString()` — see the file note. */
export function isoDayOf(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

const shift = (date: Date, days: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)

/** Monday of the week containing `date`. */
const mondayOf = (date: Date) => shift(date, -((date.getDay() + 6) % 7))

/**
 * Resolve a preset against a reference day. `custom` returns null — it has no
 * derivation, which is exactly what makes it custom.
 */
export function resolveRangePreset(id: DateRangePresetId, today: Date = new Date()): DateRangeValue | null {
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  switch (id) {
    case 'today':
      return { from: isoDayOf(base), to: isoDayOf(base) }
    case 'yesterday': {
      const day = shift(base, -1)
      return { from: isoDayOf(day), to: isoDayOf(day) }
    }
    case 'this-week':
      return { from: isoDayOf(mondayOf(base)), to: isoDayOf(base) }
    case 'last-week': {
      const monday = shift(mondayOf(base), -7)
      return { from: isoDayOf(monday), to: isoDayOf(shift(monday, 6)) }
    }
    case 'this-month':
      return { from: isoDayOf(new Date(base.getFullYear(), base.getMonth(), 1)), to: isoDayOf(base) }
    case 'last-month': {
      const first = new Date(base.getFullYear(), base.getMonth() - 1, 1)
      const last = new Date(base.getFullYear(), base.getMonth(), 0)
      return { from: isoDayOf(first), to: isoDayOf(last) }
    }
    // Inclusive of today, so "last 7 days" is seven days and not eight.
    case 'last-7':
      return { from: isoDayOf(shift(base, -6)), to: isoDayOf(base) }
    case 'last-30':
      return { from: isoDayOf(shift(base, -29)), to: isoDayOf(base) }
    case 'last-90':
      return { from: isoDayOf(shift(base, -89)), to: isoDayOf(base) }
    case 'custom':
      return null
    default:
      return null
  }
}

/** Inclusive day count, or null when either end is unparseable. */
export function rangeSpanDays(range: DateRangeValue): number | null {
  const from = Date.parse(`${range.from}T00:00:00`)
  const to = Date.parse(`${range.to}T00:00:00`)
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null
  return Math.round((to - from) / 86_400_000) + 1
}

/**
 * Split offered ids into list chips vs From/To form.
 * `presets={['custom']}` → customOnly (ticker desks: no Today/Yesterday list).
 */
export function splitDateRangePresets(offered: DateRangePresetId[]): {
  presetChoices: DateRangePresetId[]
  showCustom: boolean
  customOnly: boolean
} {
  const presetChoices = offered.filter((id) => id !== 'custom')
  const showCustom = offered.length === 0 || offered.includes('custom')
  return {
    presetChoices,
    showCustom,
    customOnly: showCustom && presetChoices.length === 0,
  }
}

export interface DateRangePickerProps {
  preset: DateRangePresetId
  value: DateRangeValue
  onChange: (next: { preset: DateRangePresetId } & DateRangeValue) => void
  /** Which presets to offer, in order. Defaults to all ten. Pass `['custom']` for From/To only. */
  presets?: DateRangePresetId[]
  /** Pre-formatted resolved range for the trigger's second line, e.g. "20 – 26 Jul 2026". */
  summary?: ReactNode
  /** Extra line under a preset, e.g. why last-week is the settling window. */
  presetHint?: (id: DateRangePresetId, range: DateRangeValue | null) => ReactNode
  label?: string
  /** Longest range accepted. The fixture and the analytics endpoints both degrade past a year. */
  maxSpanDays?: number
  align?: 'start' | 'end'
  /** Reference "today". Injectable so the component is testable without faking the clock. */
  today?: Date
  className?: string
}

export function DateRangePicker({
  preset,
  value,
  onChange,
  presets,
  summary,
  presetHint,
  label = 'Date range',
  maxSpanDays = 366,
  align = 'start',
  today,
  className,
}: DateRangePickerProps) {
  const fieldId = useId()
  const anchor = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<DateRangeValue>(value)
  const [error, setError] = useState<string | null>(null)

  // The draft is the two half-typed inputs, nothing more. Re-seed it whenever
  // the committed range changes, so reopening never shows a stale attempt.
  useEffect(() => {
    if (open) {
      setDraft(value)
      setError(null)
    }
  }, [open, value])

  const offered = presets ?? DATE_RANGE_PRESETS.map((p) => p.id)
  const { presetChoices, showCustom, customOnly } = splitDateRangePresets(offered)

  const pick = (id: DateRangePresetId) => {
    if (id === 'custom') {
      setDraft(value)
      return
    }
    const range = resolveRangePreset(id, today)
    if (!range) return
    onChange({ preset: id, ...range })
    setOpen(false)
  }

  const applyCustom = () => {
    if (draft.from > draft.to) {
      setError('The start date must be on or before the end date.')
      return
    }
    const span = rangeSpanDays(draft)
    if (span === null) {
      setError('Enter both dates.')
      return
    }
    if (span > maxSpanDays) {
      setError(`Choose a range of ${maxSpanDays >= 365 ? 'a year' : `${maxSpanDays} days`} or less.`)
      return
    }
    onChange({ preset: 'custom', ...draft })
    setOpen(false)
  }

  const activeLabel = DATE_RANGE_PRESETS.find((p) => p.id === preset)?.label ?? 'Custom'
  const triggerLabel = customOnly
    ? String(summary ?? (value.from && value.to ? `${value.from} – ${value.to}` : label))
    : activeLabel

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={cx('w7-rangetrigger', className)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${triggerLabel}`}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="calendar" size={16} />
        <span className="w7-rangetrigger-text">
          {customOnly ? (
            <span className="w7-rangetrigger-preset">{summary ?? triggerLabel}</span>
          ) : (
            <>
              <span className="w7-rangetrigger-preset">{activeLabel}</span>
              {summary ? <span className="w7-rangetrigger-sub">{summary}</span> : null}
            </>
          )}
        </span>
        <Icon name="chevron-down" size={14} />
      </button>

      <Popover open={open} onClose={() => setOpen(false)} anchorRef={anchor} label={label} align={align} width={300}>
        {presetChoices.length > 0 ? (
          <ul className="w7-rangelist">
            {presetChoices.map((id) => {
              const range = resolveRangePreset(id, today)
              const hint = presetHint?.(id, range)
              return (
                <li key={id}>
                  <button
                    type="button"
                    className="w7-rangeoption"
                    aria-pressed={preset === id}
                    onClick={() => pick(id)}
                  >
                    <span>{DATE_RANGE_PRESETS.find((p) => p.id === id)?.label ?? id}</span>
                    {hint ? <span className="w7-rangeoption-hint">{hint}</span> : null}
                    {preset === id ? <Icon name="check" size={14} /> : null}
                  </button>
                </li>
              )
            })}
          </ul>
        ) : null}

        {showCustom ? (
          <div className="w7-rangecustom" data-solo={customOnly ? 'true' : undefined}>
            <label className="w7-field-label" htmlFor={`${fieldId}-from`}>
              From
            </label>
            <input
              id={`${fieldId}-from`}
              className="w7-input"
              type="date"
              value={draft.from}
              onChange={(event) => setDraft((d) => ({ ...d, from: event.target.value }))}
            />
            <label className="w7-field-label" htmlFor={`${fieldId}-to`}>
              To
            </label>
            <input
              id={`${fieldId}-to`}
              className="w7-input"
              type="date"
              value={draft.to}
              onChange={(event) => setDraft((d) => ({ ...d, to: event.target.value }))}
            />
            {error ? (
              <p className="w7-field-error" role="alert">
                <Icon name="error" size={13} />
                {error}
              </p>
            ) : null}
            <Button variant="primary" size="sm" onClick={applyCustom}>
              Apply
            </Button>
          </div>
        ) : null}
      </Popover>
    </>
  )
}
